import { useNavigate, useParams } from "react-router-dom";
import { Product, Store } from "../../../../__generated__/graphql";
import { StoreCardForTimesail } from "../../../../components/store-card/store-card-for-timesail";
import { StoreCardForPack } from "../../../../components/store-card/store-card-for-pack";
import { StoreCardForTimesailYet } from "../../../../components/store-card/store-card-for-timesail-yet";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import { useQuery } from "@apollo/client";
import { GET_STORE } from "../../../../api/store-queries";
import { GET_SELLER_STORE } from "../../api/seller-store";
import {
  getReserveTime,
  getReserveTimeForTomorrow,
} from "../../../../utils/reserve-time";
import {
  canPackExpose,
  getExposeType,
} from "../../../../utils/store-expose";
import Header from "../../../../components/common/header";

const StorePreviewCardWrapper = ({
  children,
  isLiveSalesVisible,
  isPreorderSalesVisible,
}: {
  children: React.ReactNode;
  isLiveSalesVisible: boolean;
  isPreorderSalesVisible: string;
}) => {
  return (
    <div className="bg-white flex flex-col h-screen">
      <Header title="내 매장 노출 보기 (카드)" showBackButton />

      <div className="flex flex-col h-full items-center justify-center px-4 gap-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-4 border-2 border-gray-200 p-2 rounded-lg">
            <div
              className={`w-6 h-6 rounded-full transition-colors shadow-sm ${
                isLiveSalesVisible
                  ? "bg-blue-500 ring-2 ring-blue-200"
                  : "bg-gray-500 ring-2 ring-gray-200"
              }`}
            ></div>
            <div className="font-semibold text-2xl">
              {isLiveSalesVisible ? "실시간 판매 진행중" : "실시간 판매 대기중"}
            </div>
          </div>

          <div className="flex items-center gap-4 border-2 border-gray-200 p-2 rounded-lg">
            <div
              className={`w-6 h-6 rounded-full transition-colors duration-300 shadow-sm ${
                isPreorderSalesVisible === "오늘" ||
                isPreorderSalesVisible === "내일"
                  ? "bg-green-500 ring-2 ring-green-200"
                  : "bg-gray-500 ring-2 ring-gray-200"
              }`}
            ></div>
            <div className="font-semibold text-2xl">
              {isPreorderSalesVisible === "오늘"
                ? "예약 판매 노출중 (오늘)"
                : isPreorderSalesVisible === "내일"
                ? "예약 판매 노출중 (내일)"
                : "예약 판매 미노출"}
            </div>
          </div>

          {/* 실시간 판매, 예약 판매 동시 진행 시에만 표시 */}
          {isLiveSalesVisible && isPreorderSalesVisible && (
            <div>
              <p className="text-gray-700 break-keep text-md text-center">
                · 예약판매와 실시간판매가 동시에 진행중일 때는
                <br />
                <span className="font-bold">실시간판매가 우선 적용</span>돼요.
              </p>
            </div>
          )}
        </div>

        <div className="w-full">
          <div className="mb-4">
            <p className="text-gray-700 break-keep text-2xl text-center">
              "아래는{" "}
              <span className="font-bold">실제 고객에게 보이는 화면</span>
              이에요!"
            </p>
          </div>

          <div className="h-56">{children}</div>

          <div className="text-center mt-6 border p-2 rounded-lg bg-gray-50 mb-4">
            <p className="text-gray-700 break-keep text-2xl">
              ⬆️ 클릭해보세요!
            </p>
            <p className="text-gray-700 break-keep text-xl mt-1">
              매장을 눌러 <span className="font-bold">상세페이지</span>를 볼 수
              있어요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function BossStorePreviewCard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading: storeLoading } = useQuery(GET_STORE, {
    variables: {
      storeId: Number(id),
    },
    fetchPolicy: "network-only",
  });
  const { data: storeData, loading: sellerStoreLoading } = useQuery(
    GET_SELLER_STORE,
    {
      fetchPolicy: "network-only",
    }
  );

  if (storeLoading || sellerStoreLoading) return <LoadingSpinnerFull />;

  const isLiveSalesVisible =
    storeData?.getSellerStore?.todaysProducts &&
    storeData?.getSellerStore?.todaysProducts.length !== 0;
  const isPreorderSalesVisible = getExposeType(storeData?.getSellerStore);
  const store = data?.store;

  if (!store) return null;

  if (store.todaysProducts?.length !== 0 && !store.isProductAllSoldOut) {
    return (
      <StorePreviewCardWrapper
        isLiveSalesVisible={isLiveSalesVisible}
        isPreorderSalesVisible={isPreorderSalesVisible}
      >
        <StoreCardForTimesail
          title={store.title as string}
          products={store.todaysProducts as Product[]}
          closingHours={store.closingHours as string}
          discountPrice={(store.todaysProducts as Product[])[0]?.userPrice}
          price={(store.todaysProducts as Product[])[0]?.price}
          distance={1} // 미리보기 페이지이므로 거리는 임의로 설정
          img={store.img as string}
          onClick={() => {
            navigate(`/boss/store-preview/detail/${store.id}`);
          }}
          likeCount={store?.likes?.length ?? 0}
          signupDate={store.createdAt}
        />
      </StorePreviewCardWrapper>
    );
  } else if (store.pack && canPackExpose(store as Store)) {
    return (
      <StorePreviewCardWrapper
        isLiveSalesVisible={isLiveSalesVisible}
        isPreorderSalesVisible={isPreorderSalesVisible}
      >
        <StoreCardForPack
          title={store.title}
          quantity={store.pack.currentCount}
          discountPrice={store.pack.userPrice}
          price={store.pack.price}
          isLiked={store.isLiked}
          distance={1} // 미리보기 페이지이므로 거리는 임의로 설정
          img={store.img as string}
          reserveTime={getReserveTime(store.reserveTime)}
          tomorrowReserveTime={getReserveTimeForTomorrow(store.reserveTime)}
          onClick={() => {
            navigate(`/boss/store-preview/detail/${store.id}`);
          }}
          exposeType={isPreorderSalesVisible}
          onClickHeart={async (e) => {
            e.stopPropagation(); // Prevents triggering the store click
            return Promise.resolve();
          }}
          likeCount={store?.likes?.length ?? 0}
          bestReview={store?.bestReview}
          signupDate={store.createdAt}
        />
      </StorePreviewCardWrapper>
    );
  } else {
    return (
      <StorePreviewCardWrapper
        isLiveSalesVisible={isLiveSalesVisible}
        isPreorderSalesVisible={isPreorderSalesVisible}
      >
        <StoreCardForTimesailYet
          title={store.title as string}
          closingHours={store.closingHours as string}
          distance={1} // 미리보기 페이지이므로 거리는 임의로 설정
          img={store.img as string}
          quantity={0}
          onClick={() => {}}
          isLiked={store.isLiked}
          onClickHeart={async (e) => {
            e.stopPropagation(); // Prevents triggering the store click
            return Promise.resolve();
          }}
          likeCount={store?.likes?.length ?? 0}
          signupDate={store.createdAt}
        />
      </StorePreviewCardWrapper>
    );
  }
}
